import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withStyles } from "tss-react/mui";
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import withMediaQuery from 'components/WithMediaQuery';

import DialogActions from 'components/DialogActions';
import ResponsiveDialogHeader from 'components/ResponsiveDialogHeader';
import DialogTransition from 'components/DialogTransition';
import SecondaryDialogButton from 'components/SecondaryDialogButton';
import { H6, Body1 } from 'components/Typography';
import isStringSet from 'utilities/is/isStringSet';
import isNotNullOrEmpty from 'utilities/is/isNotNullOrEmpty';
import styles from './styles';
import m from 'messages';

class CompetencyRequirementsDialog extends React.Component {

    splitByOrGroup(competences) {

        const allOf = [];
        const oneOf = {};

        competences.forEach(c => {
            if (!isStringSet(c.orGroup)) {
                allOf.push(c);
            } else {
                let group = oneOf[c.orGroup] || [];
                group.push(c);
                oneOf[c.orGroup] = group;
            }
        });

        let array = Object.keys(oneOf).map(k => oneOf[k]);

        return { allOf, oneOf: array };
    }

    competenceList(competences, groupName, contentBuilder) {
        const { allOf, oneOf } = this.splitByOrGroup(competences);

        return (
            <React.Fragment>
                {competences.length > 0 &&
                    <section className={this.props.classes.section}>
                        <H6>
                            {groupName}
                        </H6>
                        <div>
                            {isNotNullOrEmpty(allOf) &&
                                <React.Fragment>
                                    <Body1 className={this.props.classes.competenceGroupHeader}>{m.competencies.allOf}</Body1>
                                    {allOf.map((c, i) =>
                                        <React.Fragment key={i}>
                                            <Body1 className={this.props.classes.competence}>
                                                {contentBuilder(c)}
                                            </Body1>
                                            <Divider />
                                        </React.Fragment>
                                    )}
                                </React.Fragment>
                            }
                        </div>
                        <div>
                            {isNotNullOrEmpty(oneOf) &&
                                <React.Fragment>
                                    {oneOf.map((group, i) =>
                                        <React.Fragment key={i}>
                                            <Body1 className={this.props.classes.competenceGroupHeader}>{m.competencies.oneOf}</Body1>
                                            {group.map((c, j) =>
                                                <React.Fragment key={j}>
                                                    <Body1 className={this.props.classes.competence}>
                                                        {contentBuilder(c)}
                                                    </Body1>
                                                    <Divider />
                                                </React.Fragment>
                                            )}
                                        </React.Fragment>
                                    )}
                                </React.Fragment>
                            }
                        </div>
                    </section>
                }
            </React.Fragment>
        );

    }

    divisionCompetences() {

        const title = c => {
            if (c.divisionSpecific) return `${c.name} (${c.divisionName})`;
            if (c.companySpecific) return `${c.name} (${c.companyName})`;
            if (c.cscsCardSchemeID) return `Compatible Skills Card - ${c.cscsCardSchemeName} - ${c.cscsCardType}`;
            if (c.employerName) return `${c.name} (${c.employerName})`;
            return c.name;
        };

        const builder = c => <span>{title(c)}</span>;

        return this.competenceList(this.props.divisionCompetences, this.props.divisionName, builder);
    }

    projectCompetences() {

        const title = c => {
            if (c.projectSpecific) return `${c.name} (${c.projectName})`;
            if (c.companySpecific) return `${c.name} (${c.companyName})`;
            if (c.cscsCardSchemeID) return `Compatible Skills Card - ${c.cscsCardSchemeName} - ${c.cscsCardType}`;
            if (c.employerName) return `${c.name} (${c.employerName})`;
            return c.name;
        };

        const builder = c => <span>{title(c)}</span>;

        return this.competenceList(this.props.projectCompetences, this.props.projectLevelName, builder);
    }

    siteCompetences() {

        const title = c => {
            if (c.siteSpecific) return `${c.name} (${c.siteName})`;
            if (c.companySpecific) return `${c.name} (${c.companyName})`;
            if (c.divisionSpecific) return `${c.name} (${c.divisionName})`;
            if (c.cscsCardSchemeID) return `Compatible Skills Card - ${c.cscsCardSchemeName} - ${c.cscsCardType}`;
            if (c.employerName) return `${c.name} (${c.employerName})`;
            return c.name;
        };

        const builder = c => <span>{title(c)}</span>;

        return this.competenceList(this.props.siteCompetences, this.props.siteLevelName, builder);
    }

    zoneCompetences() {
        const title = c => {
            if (c.zoneSpecific) return `${c.name} (${c.zoneName})`;
            if (c.companySpecific) return `${c.name} (${c.companyName})`;
            if (c.cscsCardSchemeID) return `Compatible Skills Card ${c.cscsCardSchemeName} - ${c.cscsCardType}`;
            if (c.employerName) return `${c.name} (${c.employerName})`;
            return c.name;
        };

        const builder = c => <span>{title(c)}</span>;

        return this.competenceList(this.props.zoneCompetences, this.props.zoneLevelName, builder);
    }

    emptyMessage() {
        const count =
            this.props.zoneCompetences.length +
            this.props.siteCompetences.length +
            this.props.projectCompetences.length +
            this.props.divisionCompetences.length;

        return (
            <React.Fragment>
                {count === 0 &&
                    <Body1 className={this.props.classes.emptyMessage}>
                        {m.competencies.noRequirementsAtLocation}
                    </Body1>
                }
            </React.Fragment>
        );
    }

    render() {
        const { fullScreen } = this.props;
        return (
            <Dialog
                open={this.props.open}
                onClose={this.props.handleClose}
                TransitionComponent={DialogTransition}
                fullScreen={fullScreen}>
                <ResponsiveDialogHeader
                    showBackIcon={false}
                    fullScreen={fullScreen}
                    title={m.competencies.locationRequirements}
                    fullScreenTitle=""
                    handleClose={this.props.handleClose} />
                <DialogContent>
                    {this.divisionCompetences()}
                    {this.projectCompetences()}
                    {this.siteCompetences()}
                    {this.zoneCompetences()}
                    {this.emptyMessage()}
                </DialogContent>

                {!this.props.fullScreen &&
                    <DialogActions>
                        <SecondaryDialogButton onClick={this.props.handleClose}>
                            {m.general.close}
                        </SecondaryDialogButton>
                    </DialogActions>
                }
            </Dialog>
        );
    }
}

CompetencyRequirementsDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired
};

function mapStateToProps(state) {

    const { config, location, supervisor } = state;

    const headerFunc = m.competencies.requirements.sectionHeader;
    const zoneCode = (location.zone ? location.zone.zoneCode : '');

    return {
        divisionName: `${headerFunc(config.divisionLevelName)}`,
        projectLevelName: `${headerFunc(config.projectLevelName)}`,
        siteLevelName: `${headerFunc(config.siteLevelName)}`,
        zoneLevelName: `${headerFunc(config.zoneLevelName)}`,
        hasZone: Boolean(zoneCode),

        divisionCompetences: supervisor.divisionCompetences || [],
        projectCompetences: supervisor.projectCompetences || [],
        siteCompetences: supervisor.siteCompetences || [],
        zoneCompetences: supervisor.zoneCompetences || []
    };
}

const Media = withMediaQuery(theme => theme.breakpoints.down('lg'))(CompetencyRequirementsDialog);
const styledDialog = withStyles(Media,styles);
const Connected = connect(mapStateToProps)(styledDialog);

export default Connected;
